import React from 'react';
import Layout from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';

const TermsConditionsPage = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms and Conditions",
    "description": "Terms and Conditions for Redspire Civil Engineering services and website usage.",
    "url": "https://redspire.com/terms-and-conditions",
    "publisher": {
      "@type": "Organization",
      "name": "Redspire Civil Engineering"
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-01-01"
  };

  return (
    <Layout>
      <SEOHead
        title="Terms & Conditions | Redspire Civil Engineering"
        description="Terms and Conditions governing the use of Redspire Civil Engineering services and website. Read our terms before engaging our services."
        keywords={['terms and conditions', 'service terms', 'user agreement', 'Redspire terms']}
        url="https://redspire.com/terms-and-conditions"
        schema={schema}
      />

      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <FileText className="w-12 h-12 text-red-500" />
            <h1 className="text-4xl md:text-5xl font-bold">Terms & Conditions</h1>
          </div>
          <p className="text-xl text-gray-300">Governing the use of our services</p>
          <p className="text-sm text-gray-400 mt-4">Effective Date: January 1, 2025</p>
        </div>
      </div>

      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-lg max-w-none">
          
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms and Conditions ("Terms") govern your use of the Redspire Civil Engineering website and services. By accessing our website, requesting a quote, or engaging our services, you agree to be bound by these Terms. If you do not agree with any part of these Terms, you may not use our services.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">2. Services Provided</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Redspire Civil Engineering provides professional civil engineering services including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Infrastructure development and design</li>
              <li>Transportation engineering and traffic studies</li>
              <li>Water resources engineering and stormwater management</li>
              <li>Structural engineering and analysis</li>
              <li>Geotechnical and environmental services</li>
              <li>Construction management and inspection</li>
              <li>Site development and land development engineering</li>
              <li>Materials testing and quality assurance</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              All services are subject to written agreements, project scope definitions, and applicable professional engineering standards.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">3. Professional Licensing and Standards</h2>
            <p className="text-gray-700 leading-relaxed">
              Redspire Civil Engineering and its professional staff maintain appropriate licenses, certifications, and insurance as required by Connecticut law and applicable jurisdictions. All work is performed in accordance with applicable building codes, engineering standards (including ASCE, AASHTO, and ACI standards), and regulatory requirements.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">4. Client Responsibilities</h2>
            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.1 Information Provision</h3>
            <p className="text-gray-700 leading-relaxed">
              Clients must provide accurate, complete, and timely information necessary for project completion, including site access, existing documentation, survey data, geotechnical reports, and regulatory requirements.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.2 Decision-Making Authority</h3>
            <p className="text-gray-700 leading-relaxed">
              Clients must have authority to make decisions regarding project scope, budget, and design approvals. Unauthorized changes or scope modifications may result in additional fees and schedule impacts.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.3 Payment Obligations</h3>
            <p className="text-gray-700 leading-relaxed">
              Clients agree to pay invoices according to agreed-upon payment terms. Late payments may incur interest charges and may suspend project work until accounts are current.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">5. Fees and Payment Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Professional fees are established by written agreement and may be structured as:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Lump sum for defined scope of work</li>
              <li>Hourly rates for time-and-materials projects</li>
              <li>Percentage of construction cost</li>
              <li>Retainer-based arrangements</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Invoices are typically issued monthly or upon milestone completion. Payment is due within 30 days unless otherwise specified. Additional services beyond the original scope will be billed separately.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">6. Project Scope and Changes</h2>
            <p className="text-gray-700 leading-relaxed">
              The scope of services will be defined in a written proposal or contract. Any changes to project scope, schedule, or budget require written approval from both parties. Scope changes may affect project timeline and cost. Emergency or unforeseen conditions may necessitate immediate action, which will be documented and billed accordingly.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">7. Intellectual Property Rights</h2>
            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">7.1 Ownership of Deliverables</h3>
            <p className="text-gray-700 leading-relaxed">
              Upon full payment, clients receive a license to use project deliverables (drawings, specifications, reports) for the specific project and site. Redspire retains ownership of underlying methodologies, templates, and proprietary tools.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">7.2 Reuse Restrictions</h3>
            <p className="text-gray-700 leading-relaxed">
              Deliverables may not be reused for other projects or sites without Redspire's written consent and may require additional fees. Unauthorized reuse releases Redspire from liability for such use.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">7.3 Third-Party Materials</h3>
            <p className="text-gray-700 leading-relaxed">
              Clients are responsible for obtaining necessary rights to any third-party materials (surveys, geotechnical reports, architectural drawings) incorporated into project deliverables.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To the maximum extent permitted by law:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Redspire's total liability for any claims arising from services shall not exceed the total fees paid for the specific project</li>
              <li>Redspire shall not be liable for consequential, indirect, incidental, special, or punitive damages</li>
              <li>Claims must be brought within two (2) years of substantial completion of services</li>
              <li>Liability is limited to direct damages resulting from Redspire's negligent acts or omissions</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              This limitation applies regardless of the form of action, whether in contract, tort, strict liability, or otherwise.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">9. Warranties and Disclaimers</h2>
            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">9.1 Standard of Care</h3>
            <p className="text-gray-700 leading-relaxed">
              Redspire warrants that services will be performed with the standard of care and diligence normally practiced by recognized professional engineering firms performing similar services under similar circumstances. This is the sole warranty provided.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">9.2 No Absolute Warranties</h3>
            <p className="text-gray-700 leading-relaxed">
              Redspire makes no warranties, express or implied, regarding project outcomes, construction costs, regulatory approvals, or fitness for particular purposes. Engineering involves professional judgment based on available information and cannot guarantee specific results.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">9.3 Third-Party Information</h3>
            <p className="text-gray-700 leading-relaxed">
              Redspire relies on information provided by clients and third parties (surveys, geotechnical investigations, utility locations) and is not responsible for inaccuracies in such information unless such information was prepared by Redspire.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">10. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              To the extent permitted by law, clients agree to indemnify and hold harmless Redspire from claims arising from: (a) modifications to Redspire's work products without Redspire's consent; (b) client's failure to provide accurate information; (c) use of deliverables for purposes other than the original project; (d) client's failure to follow Redspire's recommendations.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">11. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              Either party may terminate services upon 14 days written notice. Upon termination, client shall pay for services performed through the effective termination date, plus reasonable wind-down costs. Redspire will deliver work product completed through termination, subject to payment of all fees due.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">12. Dispute Resolution</h2>
            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">12.1 Negotiation</h3>
            <p className="text-gray-700 leading-relaxed">
              Parties agree to attempt resolution of disputes through good-faith negotiation before pursuing formal proceedings.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">12.2 Mediation</h3>
            <p className="text-gray-700 leading-relaxed">
              If negotiation fails, parties agree to mediation before an agreed-upon neutral mediator before initiating litigation or arbitration.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">12.3 Governing Law and Venue</h3>
            <p className="text-gray-700 leading-relaxed">
              These Terms are governed by Connecticut law. Any litigation shall be brought in the state or federal courts located in Fairfield County, Connecticut.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">13. Confidentiality</h2>
            <p className="text-gray-700 leading-relaxed">
              Both parties agree to maintain confidentiality of proprietary and confidential information. This obligation does not apply to information that: (a) is publicly available; (b) is independently developed; (c) is disclosed with permission; (d) must be disclosed by law or regulation.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">14. Website Use</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Use of the Redspire website is subject to these additional terms:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Content is for informational purposes only and does not constitute professional advice</li>
              <li>Website content may not be reproduced without written permission</li>
              <li>Links to third-party sites are provided for convenience; Redspire is not responsible for external content</li>
              <li>Website availability is not guaranteed; maintenance or technical issues may cause temporary unavailability</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">15. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              Redspire reserves the right to modify these Terms at any time. Changes will be effective upon posting to the website. Continued use of services after changes constitutes acceptance of modified Terms. Material changes to existing service agreements require written consent from both parties.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">16. Severability</h2>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is found invalid or unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">17. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed">
              For questions about these Terms, please contact:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mt-4">
              <p className="text-gray-900 font-semibold mb-2">Redspire Civil Engineering</p>
              <p className="text-gray-700">218 Brookside Industrial Park</p>
              <p className="text-gray-700">Stamford, CT 06902</p>
              <p className="text-gray-700 mt-3">Email: <a href="mailto:info@redspire.com" className="text-red-600 hover:underline">info@redspire.com</a></p>
              <p className="text-gray-700">Phone: <a href="tel:+12034963375" className="text-red-600 hover:underline">+1 (203) 496-3375</a></p>
            </div>
          </section>

          <div className="border-t border-gray-200 pt-8 mt-12">
            <p className="text-sm text-gray-600">
              Last Updated: January 1, 2025
            </p>
            <p className="text-sm text-gray-600 mt-2">
              © 2025 Redspire Civil Engineering. All rights reserved.
            </p>
          </div>

        </div>
      </article>
    </Layout>
  );
};

export default TermsConditionsPage;
